import { useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { AxiosError } from 'axios';

import backend from '../backend';

export type PedidoItem = {
  id: number;
  pedido_id: number;
  marketplace_item_id: string;
  marketplace_variation_id?: string | null;
  produto_id?: number | null;
  anuncio_id?: number | null;
  titulo: string;
  sku?: string | null;
  quantidade: number;
  preco_unitario: string;
  total: string;
};

type InfoAdicionalFiscal = {
  type: string;
  value?: string;
};

type EnderecoFiscalMercadoLivre = {
  street_name?: string;
  street_number?: string | number;
  neighborhood?: string;
  city_name?: string;
  state?: { name?: string };
  zip_code?: string;
  comment?: string;
  country_id?: string;
};

type BillingInfoFiscal = {
  name?: string;
  last_name?: string;
  identification?: {
    type?: string;
    number?: string;
  };
  address?: EnderecoFiscalMercadoLivre;
  additional_info?: InfoAdicionalFiscal[];
  doc_type?: string;
  doc_number?: string;
};

type BillingInfoResponse = {
  erro?: boolean;
  message?: string;
  billing_info?: BillingInfoFiscal;
  buyer?: {
    billing_info?: BillingInfoFiscal;
  };
};

type CompradorMercadoLivre = {
  id?: string | number;
  nickname?: string;
  billing_info?: { id?: string | number };
};

type VendedorMercadoLivre = {
  id?: string | number;
  nickname?: string;
};

type PagamentoMercadoLivre = {
  id?: string | number;
  total_paid_amount?: string | number;
  transaction_amount_refunded?: string | number;
};

type EnvioMercadoLivre = {
  id?: string | number;
};

type PedidoMercadoLivre = {
  buyer?: CompradorMercadoLivre;
  seller?: VendedorMercadoLivre;
  payments?: PagamentoMercadoLivre[];
  shipping?: EnvioMercadoLivre;
};

type MercadoLivreDetalhe = {
  pedido?: PedidoMercadoLivre;
  billing_info_legado?: BillingInfoResponse;
  billing_info_novo?: BillingInfoResponse;
  erro?: boolean;
  message?: string;
};

type ValorInterno = string | number;

export type PedidoDetalhe = {
  id: number;
  empresa_id: number;
  filial_id: number;
  marketplace: string;
  marketplace_order_id: string;
  status: ValorInterno;
  status_label?: string | null;
  status_cor?: string | null;
  status_pagamento?: ValorInterno | null;
  status_pagamento_label?: string | null;
  status_envio?: ValorInterno | null;
  status_envio_label?: string | null;
  substatus_envio?: string | null;
  total: string;
  moeda?: string | null;
  comprador_nome?: string | null;
  comprador_email?: string | null;
  data_pedido?: string | null;
  data_aprovacao?: string | null;
  estoque_processado: boolean;
  estoque_processado_em?: string | null;
  estoque_estornado?: boolean;
  estoque_estornado_em?: string | null;
  sincronizado_em?: string | null;
  created_at: string;
  updated_at: string;
  itens: PedidoItem[];
  mercado_livre?: MercadoLivreDetalhe | null;
  comprador_id_externo?: string | null;
  comprador_nickname?: string | null;
  comprador_sobrenome?: string | null;
  comprador_documento_tipo?: string | null;
  comprador_documento?: string | null;
  pagamento_id_externo?: string | null;
  metodo_pagamento?: ValorInterno | null;
  metodo_pagamento_label?: string | null;
  tipo_pagamento?: ValorInterno | null;
  tipo_pagamento_label?: string | null;
  status_pagamento_detalhe?: ValorInterno | null;
  status_pagamento_detalhe_label?: string | null;
  valor_pago?: string | null;
  valor_estornado?: string | null;
  envio_id_externo?: string | null;
  codigo_rastreio?: string | null;
  transportadora?: string | null;
  url_rastreio?: string | null;
  data_envio?: string | null;
  data_entrega_prevista?: string | null;
  data_entrega?: string | null;
  vendedor_id_externo?: string | null;
  vendedor_nickname?: string | null;
  status_pos_venda?: ValorInterno | null;
  status_pos_venda_label?: string | null;
  status_pos_venda_cor?: string | null;
  observacao_interna?: string | null;
  conferido?: boolean;
  conferido_em?: string | null;
  finalizado_em?: string | null;
  atendimento_atualizado_em?: string | null;
  atendimento_atualizado_por?: number | null;
  endereco_fiscal?: {
    rua?: string | null;
    numero?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    estado?: string | null;
    estado_codigo?: string | null;
    cep?: string | null;
    pais?: string | null;
    complemento?: string | null;
  } | null;
};

export type DadosPosVenda = {
  status_pos_venda?: number | null;
  observacao_interna?: string | null;
  conferido?: boolean;
};

export type DadosCancelamentoPedido = {
  reason: string;
  message?: string;
  restock_item?: boolean;
};

export const formatarData = (valor?: string | null) => {
  if (!valor) return '-';

  const data = new Date(valor);

  if (Number.isNaN(data.getTime())) return '-';

  return data.toLocaleString('pt-BR');
};

export const formatarValor = (valor?: string | number | null) => {
  const numero = Number(valor ?? 0);

  return numero.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export const obterNomeCanal = (marketplace?: string) => {
  if (marketplace === 'mercadolivre') return 'Mercado Livre';

  return marketplace || '-';
};

const obterInfoAdicional = (
  dadosFiscaisLegado: BillingInfoResponse | undefined,
  tipo: string,
) => {
  const lista = dadosFiscaisLegado?.billing_info?.additional_info || [];
  const item = lista.find(info => info.type === tipo);

  return item?.value || '';
};

const montarDadosPedido = (pedido: PedidoDetalhe | null) => {
  const pedidoMercadoLivre = pedido?.mercado_livre?.pedido;
  const dadosFiscaisLegado = pedido?.mercado_livre?.billing_info_legado;
  const dadosFiscaisNovo = pedido?.mercado_livre?.billing_info_novo;

  const compradorMercadoLivre = pedidoMercadoLivre?.buyer;
  const vendedorMercadoLivre = pedidoMercadoLivre?.seller;
  const pagamentoMercadoLivre = pedidoMercadoLivre?.payments?.[0];
  const envioMercadoLivre = pedidoMercadoLivre?.shipping;

  const dadosFiscaisDisponiveis =
    dadosFiscaisNovo && !dadosFiscaisNovo?.erro
      ? dadosFiscaisNovo
      : dadosFiscaisLegado && !dadosFiscaisLegado?.erro
      ? dadosFiscaisLegado
      : null;

  const erroDadosFiscais =
    dadosFiscaisNovo?.erro || dadosFiscaisLegado?.erro
      ? dadosFiscaisNovo?.message || dadosFiscaisLegado?.message
      : null;

  const informacoesFiscais =
    dadosFiscaisDisponiveis?.buyer?.billing_info ||
    dadosFiscaisDisponiveis?.billing_info ||
    null;

  const enderecoFiscalSalvo = pedido?.endereco_fiscal;
  const enderecoFiscal = informacoesFiscais?.address;

  const nomeFiscal =
    pedido?.comprador_nome ||
    informacoesFiscais?.name ||
    obterInfoAdicional(dadosFiscaisLegado, 'FIRST_NAME');

  const sobrenomeFiscal =
    pedido?.comprador_sobrenome ||
    informacoesFiscais?.last_name ||
    obterInfoAdicional(dadosFiscaisLegado, 'LAST_NAME');

  const documentoTipo =
    pedido?.comprador_documento_tipo ||
    informacoesFiscais?.identification?.type ||
    dadosFiscaisLegado?.billing_info?.doc_type ||
    obterInfoAdicional(dadosFiscaisLegado, 'DOC_TYPE');

  const documentoNumero =
    pedido?.comprador_documento ||
    informacoesFiscais?.identification?.number ||
    dadosFiscaisLegado?.billing_info?.doc_number ||
    obterInfoAdicional(dadosFiscaisLegado, 'DOC_NUMBER');

  const rua =
    enderecoFiscalSalvo?.rua ||
    enderecoFiscal?.street_name ||
    obterInfoAdicional(dadosFiscaisLegado, 'STREET_NAME');

  const numero =
    enderecoFiscalSalvo?.numero ||
    enderecoFiscal?.street_number ||
    obterInfoAdicional(dadosFiscaisLegado, 'STREET_NUMBER');

  const bairro =
    enderecoFiscalSalvo?.bairro ||
    enderecoFiscal?.neighborhood ||
    obterInfoAdicional(dadosFiscaisLegado, 'NEIGHBORHOOD');

  const cidade =
    enderecoFiscalSalvo?.cidade ||
    enderecoFiscal?.city_name ||
    obterInfoAdicional(dadosFiscaisLegado, 'CITY_NAME');

  const estado =
    enderecoFiscalSalvo?.estado ||
    enderecoFiscal?.state?.name ||
    obterInfoAdicional(dadosFiscaisLegado, 'STATE_NAME');

  const cep =
    enderecoFiscalSalvo?.cep ||
    enderecoFiscal?.zip_code ||
    obterInfoAdicional(dadosFiscaisLegado, 'ZIP_CODE');

  const complemento =
    enderecoFiscalSalvo?.complemento ||
    enderecoFiscal?.comment ||
    obterInfoAdicional(dadosFiscaisLegado, 'COMMENT') ||
    '-';

  const pais =
    enderecoFiscalSalvo?.pais ||
    enderecoFiscal?.country_id ||
    obterInfoAdicional(dadosFiscaisLegado, 'COUNTRY_ID') ||
    '-';

  return {
    totalItens:
      pedido?.itens.reduce(
        (total, item) => total + Number(item.quantidade || 0),
        0,
      ) || 0,

    comprador: {
      nome:
        [nomeFiscal, sobrenomeFiscal].filter(Boolean).join(' ') ||
        pedido?.comprador_nickname ||
        '-',
      email: pedido?.comprador_email || '-',
      documento: documentoNumero
        ? `${documentoTipo} ${documentoNumero}`.trim()
        : '-',
      id: pedido?.comprador_id_externo || compradorMercadoLivre?.id || '-',
      nickname:
        pedido?.comprador_nickname || compradorMercadoLivre?.nickname || '-',
      billingInfoId: compradorMercadoLivre?.billing_info?.id || '-',
    },

    pagamento: {
      id: pedido?.pagamento_id_externo || pagamentoMercadoLivre?.id || '-',
      status: pedido?.status_pagamento_label || '-',
      detalhe: pedido?.status_pagamento_detalhe_label || '-',
      metodo: pedido?.metodo_pagamento_label || '-',
      tipo: pedido?.tipo_pagamento_label || '-',
      valorPago: formatarValor(
        pedido?.valor_pago ?? pagamentoMercadoLivre?.total_paid_amount,
      ),
      valorEstornado: formatarValor(
        pedido?.valor_estornado ??
          pagamentoMercadoLivre?.transaction_amount_refunded,
      ),
    },

    envio: {
      id: pedido?.envio_id_externo || envioMercadoLivre?.id || '-',
      status: pedido?.substatus_envio
        ? `${pedido.status_envio_label || '-'} (${pedido.substatus_envio})`
        : pedido?.status_envio_label || '-',
      codigoRastreio: pedido?.codigo_rastreio || '-',
      transportadora: pedido?.transportadora || '-',
      urlRastreio: pedido?.url_rastreio || '',
      dataEnvio: pedido?.data_envio || null,
      dataEntregaPrevista: pedido?.data_entrega_prevista || null,
      dataEntrega: pedido?.data_entrega || null,
      vendedorId:
        pedido?.vendedor_id_externo || vendedorMercadoLivre?.id || '-',
      vendedorNickname:
        pedido?.vendedor_nickname || vendedorMercadoLivre?.nickname || '-',
    },

    fiscal: {
      disponivel: Boolean(
        dadosFiscaisDisponiveis ||
          pedido?.comprador_documento ||
          pedido?.endereco_fiscal,
      ),
      erro: erroDadosFiscais,
      nome: nomeFiscal || '-',
      sobrenome: sobrenomeFiscal || '-',
      documento: documentoNumero || '-',
      documentoTipo: documentoTipo || '-',
      endereco: [rua, numero, bairro, cidade, estado, cep]
        .filter(Boolean)
        .join(', '),
      complemento,
      cidade: cidade || '-',
      bairro: bairro || '-',
      estado: estado || '-',
      cep: cep || '-',
      pais,
    },
  };
};

export function usePedidoDetalhado(id?: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const pedidoQuery = useQuery<PedidoDetalhe | null>(
    ['pedido-detalhado', id],
    async () => {
      const response = await backend.get(`/pedidos/${id}`);

      return response.data?.data || null;
    },
    {
      enabled: Boolean(id),
      onError: () => {
        toast({
          title: 'Erro ao carregar pedido.',
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    },
  );

  const salvarPosVendaMutation = useMutation(
    async (dadosPosVenda: DadosPosVenda) => {
      const response = await backend.put(
        `/pedidos/${id}/pos-venda`,
        dadosPosVenda,
      );

      return response.data;
    },
    {
      onSuccess: response => {
        const pedidoAtualizado = response?.data || null;

        queryClient.setQueryData(['pedido-detalhado', id], pedidoAtualizado);

        toast({
          title: response?.message || 'Pós-venda atualizado com sucesso.',
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
      },
      onError: () => {
        toast({
          title: 'Erro ao atualizar pós-venda.',
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    },
  );

  const cancelarPedidoMutation = useMutation(
    async (dadosCancelamento: DadosCancelamentoPedido) => {
      const response = await backend.post(
        `/mercadolivre/pedidos/${id}/cancelar`,
        dadosCancelamento,
      );

      return response.data;
    },
    {
      onSuccess: response => {
        const pedidoAtualizado = response?.pedido || null;

        if (pedidoAtualizado) {
          queryClient.setQueryData(['pedido-detalhado', id], pedidoAtualizado);
        }

        toast({
          title: response?.message || 'Pedido cancelado com sucesso.',
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
      },
      onError: (error: AxiosError<{ message?: string }>) => {
        toast({
          title: error.response?.data?.message || 'Erro ao cancelar pedido.',
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    },
  );

  const salvarPosVenda = useCallback(
    async (dadosPosVenda: DadosPosVenda) => {
      if (!id) return null;

      try {
        const response = await salvarPosVendaMutation.mutateAsync(
          dadosPosVenda,
        );

        return response?.data || null;
      } catch {
        return null;
      }
    },
    [id, salvarPosVendaMutation],
  );

  const cancelarPedido = useCallback(
    async (dadosCancelamento: DadosCancelamentoPedido) => {
      if (!id) return null;

      try {
        const response = await cancelarPedidoMutation.mutateAsync(
          dadosCancelamento,
        );

        return response?.pedido || null;
      } catch {
        return null;
      }
    },
    [id, cancelarPedidoMutation],
  );

  const pedido = pedidoQuery.data || null;
  const dados = montarDadosPedido(pedido);

  return {
    pedido,
    dados,
    estaCarregando: pedidoQuery.isLoading,
    estaSalvandoPosVenda: salvarPosVendaMutation.isLoading,
    estaCancelandoPedido: cancelarPedidoMutation.isLoading,
    salvarPosVenda,
    cancelarPedido,
  };
}
